import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Database, FileCheck2, Sigma, AlertTriangle, BookOpen, Mail, Scale } from "lucide-react";
import { Card } from "@/components/ui/card";

/**
 * Public integrity commitment page. Linked from the global footer. The whole
 * point of this page is to make "ForexMind is deceptive" structurally false:
 * every claim here is something the codebase actually enforces (see
 * market_view_ledger, performance_snapshots, the resolve-signals cron, and
 * the compliance test suite).
 */
export const Route = createFileRoute("/integrity")({
  head: () => ({
    meta: [
      { title: "Our Integrity Commitment — ForexMind AI" },
      {
        name: "description",
        content:
          "How ForexMind AI tracks, calculates, and discloses performance. No fabricated numbers. Real data or an em-dash — never both.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "ForexMind AI" },
      { property: "og:title", content: "Our Integrity Commitment — ForexMind AI" },
      {
        property: "og:description",
        content:
          "Zero-fabrication policy, how performance is tracked, how it is calculated, data sources, degraded-data handling, AI limitations, and responsible disclosure.",
      },
      { property: "og:url", content: "https://forexmind.ai/integrity" },
      { property: "og:image", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:secure_url", content: "https://forexmind.ai/og-image.png" },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Our Integrity Commitment — ForexMind AI" },
      {
        name: "twitter:description",
        content:
          "Zero-fabrication policy, how performance is tracked, how it is calculated, data sources, degraded-data handling, AI limitations, and responsible disclosure.",
      },
      { name: "twitter:image", content: "https://forexmind.ai/og-image.png" },
    ],
    links: [{ rel: "canonical", href: "https://forexmind.ai/integrity" }],
  }),
  component: IntegrityPage,
});

type Section = {
  icon: typeof ShieldCheck;
  title: string;
  body: React.ReactNode;
};

const SECTIONS: Section[] = [
  {
    icon: ShieldCheck,
    title: "Zero Fabrication Policy",
    body: (
      <>
        No metric on this site is ever fabricated, hardcoded, or back-fitted.
        Either the number is computed from a real, immutable record — or we
        display an em-dash. Real data, or "—". Never both.
      </>
    ),
  },
  {
    icon: FileCheck2,
    title: "How Performance is Tracked",
    body: (
      <>
        Every market view the council produces is logged automatically to an
        append-only ledger (<code>market_view_ledger</code>). Rows are never
        deleted, never edited, and never cherry-picked. Losses, expired views,
        and degraded views are all included.
      </>
    ),
  },
  {
    icon: Sigma,
    title: "How Performance is Calculated",
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li><b>Win:</b> price reached the target zone before the invalidation level.</li>
        <li><b>Loss:</b> price reached the invalidation level before the target.</li>
        <li><b>Expired:</b> neither reached within the 48-hour resolution window.</li>
        <li><b>WAIT views:</b> excluded from accuracy entirely (no trade entered).</li>
        <li>Accuracy = wins / (wins + losses) on directional, non-degraded views.</li>
      </ul>
    ),
  },
  {
    icon: Database,
    title: "Data Sources",
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li><b>Prices:</b> Twelve Data</li>
        <li><b>Sentiment:</b> Grok / xAI (live)</li>
        <li><b>Calendar:</b> FMP</li>
        <li><b>Macro:</b> FRED</li>
        <li><b>News:</b> NewsAPI</li>
      </ul>
    ),
  },
  {
    icon: AlertTriangle,
    title: "When Data is Degraded",
    body: (
      <>
        If any required data source is unavailable, the council automatically
        forces a <b>WAIT</b> view. Degraded views are flagged on the ledger
        and excluded from official performance statistics so a bad data day
        cannot inflate or deflate the published numbers.
      </>
    ),
  },
  {
    icon: BookOpen,
    title: "Our AI Limitations",
    body: (
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <b>Order Flow Intensity</b> is a simplified educational approximation
          (BVC on OHLCV candles). It is not true tick-level VPIN.
        </li>
        <li>
          <b>Pattern similarity</b> uses reciprocal-distance matching, not a
          statistical (Pearson) correlation coefficient.
        </li>
        <li>
          Agent frameworks are <i>inspired by</i> publicly known trading
          philosophies. No endorsement, affiliation, or partnership with any
          named trader, firm, or fund is implied.
        </li>
      </ul>
    ),
  },
  {
    icon: Sigma,
    title: "Minimum Sample Size",
    body: (
      <>
        Performance statistics only display after <b>30 resolved market views</b>.
        Before that, we render an em-dash and a "Track record builds after 30
        resolved market views" note. We would rather show nothing than show a
        number with no statistical meaning.
      </>
    ),
  },
  {
    icon: Mail,
    title: "Responsible Disclosure",
    body: (
      <>
        If you find anything on this site that you believe is misleading,
        please email{" "}
        <a href="mailto:legal@forexmind.ai" className="text-primary underline">
          legal@forexmind.ai
        </a>
        . We take integrity seriously and will investigate every report.
      </>
    ),
  },
];

function IntegrityPage() {
  return (
    <main className="mx-auto min-h-screen max-w-3xl px-6 py-16">
      <div className="mb-10 flex items-center gap-3">
        <Scale className="h-7 w-7 text-primary" strokeWidth={1.4} />
        <h1 className="brand-text text-3xl font-semibold tracking-tight md:text-4xl">
          Our Integrity Commitment
        </h1>
      </div>
      <p className="mb-10 text-sm leading-relaxed text-muted-foreground">
        ForexMind AI is an educational market intelligence tool. The list below
        explains exactly how we track performance, what our AI can and cannot
        do, and what we will never do — so you can verify our claims rather
        than take our word for them.
      </p>

      <div className="space-y-4">
        {SECTIONS.map(({ icon: Icon, title, body }) => (
          <Card key={title} className="flex gap-4 p-5">
            <Icon className="mt-1 h-5 w-5 shrink-0 text-primary" strokeWidth={1.5} />
            <div className="min-w-0">
              <h2 className="brand-text mb-1 text-base font-semibold tracking-tight text-foreground">
                {title}
              </h2>
              <div className="text-sm leading-relaxed text-muted-foreground">{body}</div>
            </div>
          </Card>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap gap-3 text-sm">
        <Link to="/performance" className="text-primary underline">
          See live performance
        </Link>
        <span aria-hidden>·</span>
        <Link to="/performance-methodology" className="text-primary underline">
          Methodology
        </Link>
        <span aria-hidden>·</span>
        <Link to="/risk-disclosure" className="text-primary underline">
          Risk disclosure
        </Link>
      </div>
    </main>
  );
}
